import React, { useEffect, useState } from 'react'
import { Card, Form, Button, Row, Col, DatePicker, Spin, Table, Switch, Tabs } from 'antd'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import BaseListing from 'src/BaseListings'
import WidgetSelector from 'src/widgets/Selectors/WidgetSelector'
import {
  SOURCE_TYPE_DROPDOWN_OPTIONS,
  SOURCE_TYPE_NAMES,
  STATUS_NAME,
  STATUS_POSTED,
  VOUCHER_STATUS_DROPDOWN_OPTIONS,
} from 'src/models/vouchers'
import WidgetProjectSelector from 'src/widgets/Selectors/WidgetProjectSelector'
import { wtApiCall, wtApiPostWithContentTypeHandling } from 'src/helpers/commons/ApiWrapper'
import WidgetBranchSelector from 'src/widgets/Selectors/WidgetBranchSelector'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import { wtFormatMoney, wtFormatMoneyRoundData } from 'src/helpers/commons/utils'
import VoucherPDF from 'src/PDF/VoucherPDF'
import dayjs from 'dayjs'
import WidgetPartySelector from 'src/widgets/Selectors/WidgetPartySelector'
import WidgetAccountsSelectorVirtualized from 'src/widgets/Selectors/WidgetAccountsSelectorVirtualized'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'

const CustomerLedgerReport = () => {
  const Base = new BaseListing('customer_ledger')
  const [isLoading, setIsLoading] = useState(false)
  const [form] = Form.useForm()

  const [accounts, setAccounts] = useState([])
  const [projects, setProjects] = useState([])
  const [parties, setParties] = useState([])

  const [accountId, setAccountId] = useState()
  const [accountName, setAccountName] = useState('')
  const [projectId, setProjectId] = useState()
  const [projectName, setProjectName] = useState('')
  const [partyId, setPartyId] = useState()
  const [partyName, setPartyName] = useState('')
  const [employeeId, setEmployeeId] = useState()

  const [sourceType, setSourceType] = useState()

  const [voucherStatus, setVoucherStatus] = useState()
  const [voucherStatusName, setVoucherStatusName] = useState('')

  const [branchId, setBranchId] = useState(0)
  const [branchName, setBranchName] = useState('')
  const [CustomerLedgerReportData, setCustomerLedgerReportData] = useState([])
  const [trends, setTrends] = useState([])
  const [openingBalance, setOpeningBalance] = useState(0)
  const [closingBalance, setClosingBalance] = useState(0)

  const [searchMore, setSearchMore] = useState(false)
  const [hideClosingEntries, setHideClosingEntries] = useState(false)

  const companyName = wtGetFromLocalStorage('company_name')
  const branches = wtGetFromLocalStorage('branches')
  const [dateRange, setDateRange] = useState([]) // to hold the date range
  const [fromDate, setFromDate] = useState(null) // to hold the fromDate
  const [toDate, setToDate] = useState(null) // to hold the toDate
  const companyId = wtGetFromLocalStorage('company_id')
  const [barChart, setBarChart] = useState(false)

  // Define columns of account statement
  const CustomerLedgerReportHeaderColumns = [
    {
      title: 'Voucher No',
      dataIndex: 'voucher_no',
      key: 'voucher_no',
      fixed: 'left',
      width: 60,
      ellipsis: true,
      render: (text, record) => (
        <span
          style={{
            cursor: 'pointer',
            color: 'blue',
          }}
          onClick={() => getPdfData(record.voucher_id)}
          title="Download Voucher"
        >
          {text}
        </span>
      ),
    },
    {
      title: 'Branch',
      dataIndex: 'branch_name',
      key: 'branch_name',
      fixed: 'left',
      width: 65,
      ellipsis: true,
    },
    {
      title: 'Date',
      dataIndex: 'voucher_date',
      key: 'voucher_date',
      width: 55,
      ellipsis: true,
    },
    {
      title: 'DO #',
      dataIndex: 'delivery_no',
      key: 'delivery_no',
      width: 50,
      ellipsis: true,
      render: (text) => (
        <span style={{ color: '#0f3d75', fontWeight: 700 }}>{text}</span>
      ),
    },
    {
      title: 'Narration',
      dataIndex: 'narration',
      key: 'narration',
      width: 150,
      render: (text) => (
        <div
          style={{
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            lineHeight: 1.4,
          }}
        >
          {text}
        </div>
      ),
    },
    {
      title: 'Bank',
      dataIndex: 'bank_account',
      key: 'bank_account',
      width: 80,
      render: (text) => (
        <div
          style={{
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            lineHeight: 1.4,
          }}
        >
          {text}
        </div>
      ),
    },
    {
      title: 'Cheque No',
      dataIndex: 'cheque_no',
      key: 'cheque_no',
      width: 120,
      ellipsis: true,
    },
    {
      title: 'Cheque Date',
      dataIndex: 'cheque_date',
      key: 'cheque_date',
      width: 100,
      ellipsis: true,
    },
    {
      title: 'Project',
      dataIndex: 'project_name',
      key: 'project_name',
      width: 140,
      ellipsis: true,
    },
    {
      title: 'Source',
      dataIndex: 'source_type',
      key: 'source_type',
      width: 100,
      ellipsis: true,
      render: (text) => (typeof text === 'string' ? text.trimStart() : text),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      ellipsis: true,
    },
    {
      title: 'Created By',
      dataIndex: 'created_by',
      key: 'created_by',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Audited By',
      dataIndex: 'approved_by',
      key: 'approved_by',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Audited At',
      dataIndex: 'approved_at',
      key: 'approved_at',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Approved By',
      dataIndex: 'posted_by',
      key: 'posted_by',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Approved At',
      dataIndex: 'posted_at',
      key: 'posted_at',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Debit',
      dataIndex: 'debit',
      key: 'debit',
      width: 85,
      ellipsis: true,
      fixed: 'right',
      align: 'right',
      render: (text) => <span style={{ color: '#0f3d75' }}>{text}</span>,
    },
    {
      title: 'Credit',
      dataIndex: 'credit',
      key: 'credit',
      width: 85,
      ellipsis: true,
      fixed: 'right',
      align: 'right',
      render: (text) => <span style={{ color: '#2e8b57' }}>{text}</span>,
    },
    {
      title: 'Balance',
      dataIndex: 'showBalance',
      key: 'showBalance',
      width: 85,
      ellipsis: true,
      fixed: 'right',
      align: 'right',
      render: (text) => <span style={{ color: '#0f3d75' }}>{text}</span>,
    },
  ]

  const changeValues = (key, val, label) => {
    //Set initial values
    setOpeningBalance(0)
    setClosingBalance(0)
    setCustomerLedgerReportData([])

    if (key === 'branch_id') {
      setBranchId(val.value)
      setBranchName(val.label)
    }
    if (key === 'account_id') {
      setAccountId(val)
      if (val === undefined) {
        setAccountName('')
      } else {
        setAccountName(label[0])
      }
    }
    if (key === 'project_id') {
      setProjectId(val)
      if (val === '') {
        setProjectName('')
      }
    }
    if (key === 'party_id') {
      setPartyId(val)
      if (val === '') {
        setPartyName('')
      }
    }

    if (key === 'voucher_status') {
      setVoucherStatus(val.value)
      setVoucherStatusName(val.label)
    }
    if (key === 'date_range') {
      setDateRange(val)
    }
    if (key === 'hide_closing_entries') {
      setHideClosingEntries(val)
    }

    if (key === 'source_type') {
      setSourceType(val.value)
    }
  }

  useEffect(() => {
    Base.dropdownData(
      'accounts,projects,parties',
      { ignore_last_level: false, enable_all_levels: true },
      [setAccounts, setProjects, setParties],
      1,
    )
    getQueryParams()
  }, [])

  const getQueryParams = async () => {
    //get URL
    const url = new URL(window.location.href)

    // Get the search parameters from the URL
    const searchParams = new URLSearchParams(url.search)

    // Get the value of the 'fromDate' parameter
    let fromDate = searchParams.get('fromDate')
    let toDate = searchParams.get('toDate')
    const accountId = searchParams.get('accountId')
    const voucherStatus = searchParams.get('voucherStatus')
    const branchId = searchParams.get('branchId')
    const branchName = searchParams.get('branchName')
    const projectId =
      searchParams.get('projectId') === undefined || isNaN(searchParams.get('projectId'))
        ? 0
        : searchParams.get('projectId')
    const partyId = searchParams.get('partyId')

    const hideClosingEntries = searchParams.get('hideClosingEntries')

    if (
      fromDate !== '' &&
      toDate !== '' &&
      typeof fromDate !== 'undefined' &&
      typeof toDate !== 'undefined' &&
      fromDate !== null &&
      toDate !== null
    ) {
      fromDate = dayjs(fromDate, 'YYYY-MM-DD')
      toDate = dayjs(toDate, 'YYYY-MM-DD')
      setDateRange([fromDate, toDate])
      setHideClosingEntries(+hideClosingEntries)
    }
    if (+accountId > 0) {
      setAccountId(+accountId)
    }
    if (+voucherStatus > 0) {
      setVoucherStatus(voucherStatus)
      setVoucherStatusName(STATUS_NAME[voucherStatus])
    }
    if (+branchId > 0) {
      setBranchId(branchId)
      setBranchName(branchName)
    }
    if (+projectId !== 0) {
      setProjectId(+projectId)
    }
    if (+partyId !== 0) {
      setPartyId(+partyId)
    }
  }

  const getReportData = () => {
    if (dateRange.length < 2) {
      lzNotifyErrorMessageFromResult('Please select date range')
      return
    }
    if (!partyId || partyId === 0) {
      lzNotifyErrorMessageFromResult('Please select a party')
      return
    }
    const fromDate = dateRange[0]
    const toDate = dateRange[1]

    setIsLoading(true)
    wtApiCall(
      'reports/customer_ledger_report',
      {
        branch_id: branchId,
        from_date: fromDate.format('YYYY-MM-DD'),
        to_date: toDate.format('YYYY-MM-DD'),
        account_id: accountId,
        project_id: projectId,
        party_id: partyId,
        hide_closing_entries: hideClosingEntries,
        source_type: sourceType,
      },
      'get',
      (response) => {
        //set data
        if (response !== null) {
          if (response.data !== null) {
            let dataWithBalance = []
            if (response.data.transactional_data.length > 0) {
              // Add balance to each transactional data object
              dataWithBalance = response.data.transactional_data.map((item, index) => {
                if (index === 0) {
                  item.balance =
                    parseFloat(response.data.opening_balance) +
                    parseFloat(item.debit) -
                    parseFloat(item.credit)
                } else {
                  const prevBalance = parseFloat(
                    response.data.transactional_data[index - 1].balance,
                  )
                  item.balance = prevBalance + parseFloat(item.debit) - parseFloat(item.credit)
                }
                //Sert source type
                item.source_type = SOURCE_TYPE_NAMES[item.source_type]
                item.status = STATUS_NAME[item.status]

                //check if balance is -ve than show () in value
                item.showBalance = wtFormatMoney(item.balance, '', true, 2)

                item.debit = wtFormatMoney(item.debit, '', true, 2)
                item.credit = wtFormatMoney(item.credit, '', true, 2)
                return item
              })
            }

            // Calculate closing balance
            let closingBalance = 0
            if (dataWithBalance.length > 0) {
              closingBalance += dataWithBalance[dataWithBalance.length - 1].balance
            } else {
              closingBalance += response.data.opening_balance
            }

            closingBalance = wtFormatMoney(closingBalance, '', true, 2)

            //set all data
            setOpeningBalance(wtFormatMoney(response.data.opening_balance, '', true, 2))
            setClosingBalance(closingBalance)
            setCustomerLedgerReportData(dataWithBalance)
            setTrends(response.data.trends)
          } else {
            //Set initial values
            setOpeningBalance(0)
            setClosingBalance(0)
            setCustomerLedgerReportData([])
          }
        }

        setIsLoading(false)
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        setIsLoading(false)
      },
    )
  }

  const getPdfData = (id) => {
    wtApiCall(
      'vouchers/' + id,
      {},
      'get',
      (response) => {
        const { data } = response
        VoucherPDF(data)
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
      },
    )
  }

  const print = () => {
    wtApiPostWithContentTypeHandling('reports/customer_ledger_report/print', {
      branch_id: branchId,
      from_date: fromDate,
      to_date: toDate,
      account_id: accountId,
      project_id: projectId,
      party_id: partyId,
      hide_closing_entries: hideClosingEntries,
      source_type: sourceType,
    })
  }

  const exportToExcel = () => {
    setIsLoading(true)
    wtApiPostWithContentTypeHandling('reports/customer_ledger_report/export', {
      opening_balance: openingBalance,
      data: CustomerLedgerReportData,
      closing_balance: closingBalance,
    })
    setIsLoading(false)
  }
  let totalDebit = 0
  let totalCredit = 0

  CustomerLedgerReportData.forEach(({ debit, credit }) => {
    // Remove leading/trailing spaces and commas, then convert to a float
    const sanitizedDebit = parseFloat((debit || '0').replace(/,/g, '').trim())
    const sanitizedCredit = parseFloat((credit || '0').replace(/,/g, '').trim())

    totalDebit += sanitizedDebit
    totalCredit += sanitizedCredit
  })

  const extraRowStyle = {
    fontWeight: 'bold',
  }

  // Add the extra row
  const extraRow = {
    key: 'extra-row',
    description: 'Summary Row', // Example for a description column
    voucher_no: <span style={{ fontWeight: 'bold', color: 'black' }}>Total</span>,
    debit: (
      <span style={{ fontWeight: 'bold', color: 'black' }}>
        {wtFormatMoney(totalDebit, '', true, 2)}
      </span>
    ), // Keep two decimal places
    credit: (
      <span style={{ fontWeight: 'bold', color: 'black' }}>
        {wtFormatMoney(totalCredit, '', true, 2)}
      </span>
    ),
    style: extraRowStyle,
  }

  // Combine data with the extra row
  const enhancedDataSource = [...CustomerLedgerReportData, extraRow]

  const profitAndLossChartoptions = {
    chart: {
      type: !barChart ? 'line' : 'column',
      height: 420,
      backgroundColor: 'transparent',
      spacingTop: 20,
      spacingRight: 20,
      spacingBottom: 20,
      spacingLeft: 10,
    },
    title: {
      text: `Monthly Trend of ${accountName || 'Selected Account'}`,
      style: {
        color: '#173b63',
        fontSize: '18px',
        fontWeight: '700',
      },
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      type: 'category',
      lineColor: '#d7e4f2',
      tickColor: '#d7e4f2',
      labels: {
        style: {
          color: '#54708a',
          fontSize: '12px',
        },
      },
    },
    yAxis: {
      title: { text: 'Amount' },
      gridLineColor: '#e6eef7',
      labels: {
        formatter: function () {
          const value = this.value
          if (Math.abs(value) >= 1000000000000) {
            return (value / 1000000000000).toFixed(1) + 'T'
          } else if (Math.abs(value) >= 1000000000) {
            return (value / 1000000000).toFixed(1) + 'B'
          } else if (Math.abs(value) >= 1000000) {
            return (value / 1000000).toFixed(1) + 'M'
          } else if (Math.abs(value) >= 1000) {
            return (value / 1000).toFixed(1) + 'K'
          }
          return value.toString()
        },
        style: {
          color: '#54708a',
          fontSize: '12px',
        },
      },
    },
    legend: {
      enabled: false,
    },
    tooltip: {
      headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
      pointFormatter: function () {
        return (
          `<tr><td style="color:${this.series.color};padding:0">${this.series.name}: </td>` +
          `<td style="padding:0"><b>${wtFormatMoneyRoundData(
            this.y,
            'Rs. ',
            true,
            2,
          )}</b></td></tr>`
        )
      },
      footerFormat: '</table>',
      shared: true,
      useHTML: true,
    },
    plotOptions: {
      line: {
        lineWidth: 3,
        marker: {
          enabled: true,
          radius: 4,
          fillColor: '#ffffff',
          lineWidth: 2,
          lineColor: '#1c6bb8',
        },
      },
      column: {
        pointPadding: 0.16,
        borderWidth: 0,
        borderRadius: 6,
      },
    },
    series: [{ name: 'Total Amount', color: '#1c6bb8', data: trends }],
  }

  const items = [
    {
      key: '1',
      label: 'Statement',
      children: (
        <Table
          columns={CustomerLedgerReportHeaderColumns}
          dataSource={enhancedDataSource} // Include your extra row here
          pagination={false}
          bordered
          size="small"
          tableLayout="fixed"
          scroll={{ x: 4000, y: 400 }}
          loading={{
            indicator: (
              <div>
                <Spin />
              </div>
            ),
            spinning: isLoading,
          }}
          title={() => (
            <div style={{ textAlign: 'right' }}>
              <h6 style={{ marginBottom: 0 }}>Opening Balance: {openingBalance}</h6>
            </div>
          )}
          footer={() => (
            <div style={{ textAlign: 'right', zIndex: 0, position: 'relative' }}>
              <h6 style={{ marginBottom: 0 }}>Closing Balance: {closingBalance}</h6>
            </div>
          )}
        />
      ),
    },
    {
      key: '2',
      label: 'Trends',
      children: (
        <Card
          bordered={false}
          style={{
            borderRadius: 18,
            background: 'linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)',
            boxShadow: '0 12px 32px rgba(18, 64, 120, 0.08)',
          }}
          bodyStyle={{ padding: '1.25rem' }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
              marginBottom: 16,
              flexWrap: 'wrap',
            }}
          >
            <div>
              <div
                style={{
                  color: '#174f8a',
                  fontSize: '17px',
                  fontWeight: 700,
                }}
              >
                Trend Analysis
              </div>
              <div
                style={{
                  color: '#68809a',
                  fontSize: '12px',
                  marginTop: 4,
                }}
              >
                View monthly movement in line or bar format for the selected ledger filters.
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 12px',
                border: '1px solid #d9e7f5',
                borderRadius: 999,
                background: '#f4f9ff',
              }}
            >
              <span style={{ color: '#35526f', fontSize: 12, fontWeight: 600 }}>Chart Type</span>
              <Switch
                defaultChecked
                checkedChildren="Bar"
                unCheckedChildren="Line"
                onChange={(checked) => setBarChart(checked)}
                checked={barChart}
              />
            </div>
          </div>
          <HighchartsReact highcharts={Highcharts} options={profitAndLossChartoptions} />
        </Card>
      ),
    },
  ]

  return (
    <div
      className="container-fluid"
      style={{
        padding: '1em',
        backgroundColor: 'white',
        minHeight: '100vh',
        fontFamily: 'Helvetica',
      }}
    >
      <Form
        layout={'vertical'}
        form={form}
        onFinish={getReportData}
        style={{ maxWidth: '100%', margin: '0 auto' }}
      >
        <Card
          title={
            <span style={{ fontSize: '22px', marginLeft: '40%' }}>
              {companyName + ' Customer Ledger Report'}
            </span>
          }
          style={{ width: '100%' }}
        >
          <Row gutter={[24, 24]}>
            <Col xs={15} sm={10} md={4}>
              <label>Date Range: *</label>
              <DatePicker.RangePicker
                onChange={(value) => {
                  setDateRange(value) // Update the whole date range
                  if (value && value.length === 2) {
                    setFromDate(value[0].format('YYYY-MM-DD')) // Extract fromDate
                    setToDate(value[1].format('YYYY-MM-DD')) // Extract toDate
                  }
                }}
                style={{ width: '100%', height: 35 }}
                value={dateRange}
              />
            </Col>
            <Col xs={15} sm={10} md={8}>
              <label className="account_dropdown_label_class">Account: *</label>
              <WidgetAccountsSelectorVirtualized
                data={accounts}
                value={accountId}
                displayValue={accountName}
                height={35}
                onChange={(value, title) => changeValues('account_id', value, title)}
                reloadDropdownData={() => Base.dropdownData('accounts', {}, [setAccounts], 1)}
              />
            </Col>
            <Col xs={10} sm={10} md={6}>
              <label>Party: </label>
              <WidgetPartySelector
                onChange={(value) => changeValues('party_id', value)}
                parties={parties}
                displayValue={partyName}
                value={partyId}
                reloadDropdownData={() => Base.dropdownData('parties', {}, [setParties], 1)}
              />
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Button
                style={{ marginTop: 30, marginRight: 5 }}
                type="primary"
                htmlType="submit"
                disabled={isLoading ? true : false}
              >
                Search
              </Button>
              <Button
                style={{ marginTop: 30, marginRight: 5, color: '#fff', backgroundColor: '#E29C06' }}
                onClick={() => setSearchMore(!searchMore)}
                disabled={isLoading ? true : false}
              >
                {searchMore ? 'Less...' : 'More...'}
              </Button>
              <Button
                style={{ marginTop: 30, backgroundColor: '#4BB543', color: '#fff' }}
                onClick={exportToExcel}
                disabled={isLoading ? true : false}
              >
                Export
              </Button>

              <Button
                style={{ marginTop: 30, backgroundColor: 'red', color: '#fff' }}
                onClick={print}
                disabled={isLoading ? true : false}
              >
                Print
              </Button>
            </Col>
          </Row>

          {searchMore && (
            <Row gutter={[24, 24]}>
              <Col xs={15} sm={10} md={4}>
                <label>Branch:</label>
                <WidgetBranchSelector
                  value={branchId}
                  displayValue={branchName}
                  branches={branches}
                  placeholder="Select Branch"
                  onChange={(values) => changeValues('branch_id', values)}
                  showCloudIcon={false}
                />
              </Col>
              <Col xs={15} sm={10} md={5}>
                <label>Project: </label>
                <WidgetProjectSelector
                  onChange={(value) => changeValues('project_id', value)}
                  data={projects}
                  value={projectId}
                  displayValue={projectName}
                  height={35}
                  reloadDropdownData={() => Base.dropdownData('projects', {}, [setProjects], 1)}
                />
              </Col>

              <Col xs={18} sm={12} md={4}>
                <label>Source Type: </label>
                <WidgetSelector
                  selectedValue={sourceType}
                  displayValue={sourceType ? SOURCE_TYPE_NAMES[sourceType] : ''}
                  options={SOURCE_TYPE_DROPDOWN_OPTIONS}
                  showCloudIcon={false}
                  onChange={(values) => changeValues('source_type', values)}
                />
              </Col>

              <Col xs={9} sm={6} md={3}>
                <label>Hide Closing Entries:</label>
                <Switch
                  style={{ marginTop: 15 }}
                  value={hideClosingEntries}
                  onChange={(value) => changeValues('hide_closing_entries', value)}
                />
              </Col>
            </Row>
          )}

          <br />
          <div style={{ width: '100%' }}>
            <Tabs defaultActiveKey="1" items={items} />
          </div>
        </Card>
      </Form>
    </div>
  )
}

export default CustomerLedgerReport
